/**
 * Cosa porta un `.flw` prima di importarlo (D14) — CL07.
 *
 * Il file arriva da un'altra organizzazione, o da un'esportazione vecchia: le
 * definizioni che contiene possono riferirsi a registri che qui non esistono.
 * Importarlo zitti significherebbe scoprirlo dopo, quando un progressivo non
 * emette e nessuno sa perche'.
 *
 * Due esiti, e non si confondono:
 *
 * - **Strutturale, bloccante.** Un tipo che il contratto non conosce, id
 *   ripetuti, campi senza etichetta: si importerebbe rotto e in silenzio. Qui
 *   non c'e' niente da confermare, solo da leggere.
 * - **Registri non risolvibili, confermabile.** I campi esistono e restano, ma
 *   i loro contatori non emetteranno finche' il registro non esiste. E' una
 *   conseguenza che si puo' accettare **sapendo**, non un guasto.
 *
 * Gli id e le etichette si mostrano come sono: sono dati del file, e tradurli
 * sarebbe inventare una seconda versione di cio' che l'utente ha esportato.
 */

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import type { SchemaDocument } from "model/CustomFields";
import type { TransportIssue } from "./schemaTransport";

interface UnresolvedRegistry {
  fieldId: string;
  label: string;
  registryId: string;
}

interface ImportSchemaReportDialogProps {
  open: boolean;
  /** Il documento letto dal file, quando c'e'. */
  document: SchemaDocument | null;
  /** Il motivo per cui il file non si legge, quando e' cosi'. */
  invalidReason: "xml" | "json" | "shape" | null;
  issues: readonly TransportIssue[];
  missingRegistries: readonly UnresolvedRegistry[];
  /** L'elenco dei registri non si e' potuto leggere: si dice, non si tace. */
  registryCheckFailed?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ImportSchemaReportDialog: React.FC<ImportSchemaReportDialogProps> = ({
  open,
  document,
  invalidReason,
  issues,
  missingRegistries,
  registryCheckFailed = false,
  onConfirm,
  onCancel,
}) => {
  const translations = useTranslations();
  const blocking = invalidReason !== null || issues.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      data-testid="cf-import-report"
      id="cf-import-report-dialog"
    >
      <DialogContent>
        <Stack gap={2} pt={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {getTranslation(translations, "custom_fields.import.title")}
          </Typography>

          {invalidReason && (
            <Alert severity="error" data-testid="cf-import-invalid">
              {getTranslation(
                translations,
                `custom_fields.import.invalid.${invalidReason}`
              )}
            </Alert>
          )}

          {document && (
            <Typography variant="body2" data-testid="cf-import-fields">
              {getTranslation(translations, "custom_fields.import.fields")}
              {": "}
              {document.fields.length}
            </Typography>
          )}

          {issues.length > 0 && (
            <Alert severity="error" data-testid="cf-import-issues">
              <Stack gap={0.5}>
                {issues.map((issue) => (
                  <Typography
                    key={`${issue.code}-${issue.fieldId ?? ""}`}
                    variant="body2"
                  >
                    {/* La frase e' nel catalogo; il campo e' un dato. */}
                    {getTranslation(
                      translations,
                      `custom_fields.import.issue.${issue.code}`
                    )}
                    {issue.fieldId ? `: ${issue.fieldId}` : ""}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {!blocking && missingRegistries.length > 0 && (
            <Alert severity="warning" data-testid="cf-import-registries">
              <Stack gap={0.5}>
                <Typography variant="body2">
                  {getTranslation(translations, "custom_fields.import.registries")}
                </Typography>
                {missingRegistries.map((mancante) => (
                  <Typography key={mancante.fieldId} variant="body2">
                    <Box component="span" fontWeight="bold">
                      {mancante.label}
                    </Box>
                    {` → ${mancante.registryId}`}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}

          {!blocking && registryCheckFailed && (
            <Alert severity="warning" data-testid="cf-import-registries-unknown">
              {getTranslation(
                translations,
                "custom_fields.import.registries_unchecked"
              )}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} data-testid="cf-import-cancel">
          {getTranslation(translations, "custom_fields.action.cancel")}
        </Button>
        {/* Bloccante significa **non importabile**: un pulsante che lascia
            proseguire su un documento rotto e' la cosa che questo dialogo
            esiste per non fare. */}
        {!blocking && (
          <Button
            variant="contained"
            onClick={onConfirm}
            data-testid="cf-import-confirm"
          >
            {getTranslation(translations, "custom_fields.import.confirm")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportSchemaReportDialog;
